import ProductAvailabilityChip from '@components/Product/ProductAvailabilityChip';
import Button from '@components/v2/ui/Button/Button';
import Icon from '@components/v2/ui/Icon/Icon';
import Menu from '@components/v2/ui/Menu/Menu';
import MenuItem from '@components/v2/ui/Menu/MenuItem';
import { Box, Theme } from '@mui/material';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { isTouchEnabled } from '../../../../../../Theme';
import LangEnum from '../../../../../../lang/LangEnum';
import { CatalogProductsTestIds } from '../../../../../../utils/testIds/CatalogProducts.testIds';
import { useTranslation } from '../../../../../translation/TranslationContext';
import { CatalogListCombinationStrip } from '../hooks/useCatalogListCombinationOverflow';
import { CatalogListCombination } from '../utils/catalogListCombinations.utils';
import {
  CATALOG_LIST_CHIPS_GAP,
  CATALOG_LIST_CHIPS_STRIP_HEIGHT,
  CATALOG_LIST_ROW_TAB_INDEX,
} from '../utils/catalogListRow.consts';

interface CatalogListCombinationChipsProps extends CatalogListCombinationStrip {
  selectedKey: string;
  onSelect: (combination: CatalogListCombination) => void;
}

const touchKey = isTouchEnabled ? 'touch' : 'mouse';
const STRIP_HEIGHT = CATALOG_LIST_CHIPS_STRIP_HEIGHT[touchKey];

const STRIP_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: `${CATALOG_LIST_CHIPS_GAP}px`,
  minHeight: STRIP_HEIGHT,
  minWidth: 0,
  flexWrap: 'nowrap',
  overflow: 'hidden',
} as const;

const STACKED_STRIP_SX = { ...STRIP_SX, width: 'max-content' } as const;

const CHIP_SLOT_SX = { display: 'flex', flex: '0 0 auto' } as const;

const FOCUS_RING_INSIDE_SX = { '&.Mui-focusVisible': { outlineOffset: -2 } } as const;

export const CHIP_BUTTON_SX = {
  height: STRIP_HEIGHT,
  minWidth: 0,
  paddingBlock: 0,
  borderRadius: (theme: Theme) => `${theme.shape.borderRadiusLg}px`,
  ...FOCUS_RING_INSIDE_SX,
} as const;

const MORE_BUTTON_SX = {
  height: STRIP_HEIGHT,
  flex: '0 0 auto',
  borderRadius: (theme: Theme) => `${theme.shape.borderRadiusLg}px`,
  ...FOCUS_RING_INSIDE_SX,
} as const;

interface CombinationChipContentProps {
  combination: CatalogListCombination;
}

const CombinationChipContent = ({ combination }: CombinationChipContentProps) => (
  <ProductAvailabilityChip
    availability={combination.availability}
    showFullTypeLabel={!combination.deliveryWindow?.name}
    label={combination.deliveryWindow?.name}
    deliveryWindowStartDate={combination.deliveryWindow?.startDate}
    deliveryWindowEndDate={combination.deliveryWindow?.endDate}
  />
);

const CatalogListCombinationChips = ({
  containerRef,
  visible,
  overflow,
  isStacked,
  selectedKey,
  onSelect,
}: CatalogListCombinationChipsProps) => {
  const { translate } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isMenuOpen = Boolean(anchorEl);

  const handleOverflowSelect = (combination: CatalogListCombination) => {
    onSelect(combination);
    setAnchorEl(null);
  };

  const hasSelectedInOverflow = overflow.some(
    (combination) => combination.combinationKey === selectedKey,
  );

  return (
    <>
      <Box ref={containerRef} role="tablist" sx={isStacked ? STACKED_STRIP_SX : STRIP_SX}>
        {visible.map((combination) => (
          <Box
            key={combination.combinationKey}
            data-combination-key={combination.combinationKey}
            sx={CHIP_SLOT_SX}
          >
            <Button
              variant="text"
              color="secondary"
              size="small"
              role="tab"
              tabIndex={CATALOG_LIST_ROW_TAB_INDEX}
              isActive={combination.combinationKey === selectedKey}
              aria-selected={combination.combinationKey === selectedKey}
              sx={CHIP_BUTTON_SX}
              testId={CatalogProductsTestIds.COMBINATION_CHIP}
              onClick={() => onSelect(combination)}
            >
              <CombinationChipContent combination={combination} />
            </Button>
          </Box>
        ))}

        {overflow.length > 0 && (
          <Button
            variant="text"
            color="secondary"
            size="small"
            tabIndex={CATALOG_LIST_ROW_TAB_INDEX}
            aria-haspopup="menu"
            aria-expanded={isMenuOpen}
            isActive={isMenuOpen || hasSelectedInOverflow}
            sx={MORE_BUTTON_SX}
            testId={CatalogProductsTestIds.COMBINATION_MORE_BUTTON}
            endIcon={<Icon icon={isMenuOpen ? ChevronUp : ChevronDown} size="xs" />}
            onClick={(event) => setAnchorEl(event.currentTarget)}
          >
            {translate(LangEnum.GLOBAL_MORE)}
          </Button>
        )}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={() => setAnchorEl(null)}
      >
        {overflow.map((combination) => (
          <MenuItem
            key={combination.combinationKey}
            selected={combination.combinationKey === selectedKey}
            testId={CatalogProductsTestIds.COMBINATION_MORE_ITEM}
            onClick={() => handleOverflowSelect(combination)}
          >
            <CombinationChipContent combination={combination} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default CatalogListCombinationChips;
